"use client";

/* eslint-disable prefer-arrow-functions/prefer-arrow-functions */

import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import BreadCrumb from "@/components/breadcrumb/breadcrumb";
import { Typography } from "@/components/typography";
import { orderService } from "@/services/order";
import { InfiniteLoader } from "@/components/infinite-loader";
import { OrderCard } from "./components/order-card";

const ORDERS_PER_PAGE = 10;

export default function OrdersPage() {
  const {
    data: ordersData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery(
    ["orders-filter"],
    ({ pageParam = 1 }) =>
      orderService.filter({
        page: pageParam,
        limit: ORDERS_PER_PAGE,
      }),
    {
      getNextPageParam: (lastPage, allPages) => {
        const totalFetched = allPages.length * ORDERS_PER_PAGE;
        if (lastPage.data.count > totalFetched) {
          return allPages.length + 1;
        }
        return undefined;
      },
    },
  );

  const allOrders = useMemo(
    () => ordersData?.pages?.flatMap((page) => page.data.orders) ?? [],
    [ordersData],
  );

  const totalCount = ordersData?.pages?.[0]?.data?.count ?? 0;

  return (
    <div className="min-h-screen bg-pageBg">
      <div className="bg-breadcrumb px-2 md:px-4 border-b border-gray-200">
        <BreadCrumb
          routes={[
            { name: "Home", href: "/" },
            { name: "Orders", href: "/orders" },
          ]}
        />
      </div>
      <div className="border-t border-gray-bg2">
        <div className="container mx-auto max-w-screen-2xl px-4 py-8">
          <header className="mb-8">
            <Typography
              tag="h1"
              className="text-2xl font-bold text-black md:text-3xl my-0"
            >
              Your Orders
            </Typography>
            <Typography tag="p" className="mt-1 text-gray-500">
              {isLoading
                ? "Loading orders..."
                : `${totalCount} order${totalCount !== 1 ? "s" : ""} placed`}
            </Typography>
          </header>

          {isLoading ? (
            <div className="flex flex-col gap-6">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
                >
                  <div className="h-14 animate-pulse rounded-t-2xl bg-gray-100" />
                  <div className="flex items-center justify-between gap-4 border-b border-gray-100 px-6 py-4">
                    <div className="h-8 w-24 animate-pulse rounded-full bg-gray-200" />
                    <div className="h-4 w-32 animate-pulse rounded bg-gray-200" />
                  </div>
                  <div className="flex gap-3 overflow-hidden px-6 py-4">
                    {[1, 2].map((j) => (
                      <div
                        key={j}
                        className="h-20 w-[220px] flex-shrink-0 animate-pulse rounded-xl bg-gray-100"
                      />
                    ))}
                  </div>
                  <div className="flex items-center justify-between border-t border-gray-100 bg-[#FAFBFC] px-6 py-3">
                    <div className="h-8 w-20 animate-pulse rounded-lg bg-gray-200" />
                    <div className="h-10 w-28 animate-pulse rounded-xl bg-gray-200" />
                  </div>
                </div>
              ))}
            </div>
          ) : allOrders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Typography tag="p" className="text-lg font-medium text-gray-500">
                No orders found
              </Typography>
              <Typography tag="p" className="mt-2 text-sm text-gray-400">
                You haven&apos;t placed any orders yet.
              </Typography>
            </div>
          ) : (
            <InfiniteLoader
              loadMore={fetchNextPage}
              hasMore={hasNextPage}
              loading={isFetchingNextPage}
            >
              <div className="flex flex-col gap-6">
                {allOrders.map((order) => (
                  <OrderCard key={order.id} order={order} />
                ))}
              </div>
            </InfiniteLoader>
          )}
        </div>
      </div>
    </div>
  );
}
